import { ReactNode, forwardRef } from 'react';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { LogOut, X } from 'lucide-react';
import { NavItem, SidebarIcon } from 'src/components/Buttons/NavItem';
import { LogoLink } from 'src/components/Buttons/LogoLink';
import { useCurrentOrganization } from 'src/view/hooks/useCurrentOrganization';
import { useAvatar } from 'src/view/hooks/useAvatar';
import { useLogout } from 'src/view/hooks/useLogout';

type SidebarProps = {
    children?: ReactNode;
    avoidFooter?: boolean;
    right?: boolean;
    isOpen: boolean;
    closed?: boolean;
    toggle: () => void;
};

export const Sidebar = forwardRef<HTMLDivElement, SidebarProps>(
    ({ children, right = false, closed = false, isOpen, toggle }, ref) => {
        const { t } = useTranslation();
        const { organization } = useCurrentOrganization();
        const logout = useLogout();
        const organizationAvatarId = organization?.profile?.avatar;
        const hasOrganizationAvatar = Boolean(organizationAvatarId);
        const useOriginalLogo =
            organization?.disableLogoDesignAdjustments ?? false;
        const organizationName =
            organization?.organizationDisplayName ||
            organization?.organizationName;
        const { src } = useAvatar(organizationAvatarId, {
            firstName: organization?.profile?.firstName,
            lastName: organization?.profile?.lastName,
            displayName: organizationName,
        });

        return (
            <div
                ref={ref}
                id="sidebar"
                className={cx(
                    'flex flex-col flex-shrink-0 w-64 h-full shadow-md animate-slide-in transition-transform duration-300 ',
                    // avoidFooter && isOpen && 'pb-24 md:pb-0',
                    right &&
                        'bg-lightPeach text-woodyBrown z-30 md:shadow-none',
                    !right &&
                        'bg-lightPeach text-plum border-r border-starkWhite z-50 md:shadow-sideBarSoft',
                    right && closed && 'left-0 -translate-x-full ',
                    right &&
                        !closed &&
                        'right-0 animate-slide-in-reverse translate-x-full md:translate-x-0 md:shadow-inner',
                    right && isOpen && '-translate-x-0',
                    !right && closed && 'left-0 -translate-x-full',
                    !right &&
                        !closed &&
                        'left-0 -translate-x-full  md:translate-x-0  ',
                    !right && isOpen && !closed && '-translate-x-0 shadow-md',
                    !closed ? 'absolute md:relative' : 'absolute',
                )}
            >
                {hasOrganizationAvatar ? (
                    <div
                        className={cx('flex px-6 pt-8 pb-6', right && 'hidden')}
                    >
                        <img
                            src={src}
                            alt={organizationName || 'Organization'}
                            className={cx(
                                'max-w-full h-16 object-contain',
                                !useOriginalLogo && 'logo-tint-plum',
                            )}
                        />
                    </div>
                ) : (
                    <LogoLink
                        className={cx(
                            'flex items-center space-x-2 px-6 pt-7 pb-6 text-[32px]',
                            right && 'hidden',
                        )}
                    />
                )}
                {isOpen && (
                    <button
                        onClick={toggle}
                        className={cx(
                            'absolute md:hidden top-2  p-1  text-xs  group bg-transparent transition-all rounded-lg',
                            right && 'left-2',
                            !right && 'right-2',

                            right
                                ? 'text-woodyBrown hover:bg-woodyBrown hover:text-lightPeach'
                                : 'text-plum hover:bg-starkWhite',
                        )}
                    >
                        <SidebarIcon icon={X} />
                    </button>
                )}

                <nav
                    id="side-nav"
                    className={cx(
                        'flex flex-col flex-1 min-h-0 w-full overflow-y-auto items-center',
                        !right && 'gap-2 px-3',
                    )}
                >
                    {children}

                    <div className="mt-auto mb-8 w-full">
                        <NavItem
                            icon={LogOut}
                            label={t('account.logout')}
                            onClick={() => logout()}
                        />

                        {hasOrganizationAvatar && (
                            <div
                                className={cx('mt-4', right ? 'px-6' : 'px-3')}
                            >
                                <LogoLink className="text-[32px]" />
                            </div>
                        )}
                    </div>
                </nav>
            </div>
        );
    },
);

Sidebar.displayName = 'Sidebar';
